// AI 수요 예측 — Rockwell Plex Demand Caster 모방
// 품번 선택 → 알고리즘 토글 → 과거 + 예측 라인 + 신뢰구간 음영 + 발주 추천
import { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { Counter } from '../components/Counter'
import { Skeleton, SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface Item {
  item_code: string
  name: string
  category: 'product' | 'material' | 'consumable'
  uom: string
  standard_cost: number | null
  lead_time_days: number
  safety_stock: number
  history_days: number
}

interface Point { date: string; value: number }
interface ForecastPoint extends Point { lower?: number; upper?: number }

interface RunResp {
  forecast_id: string
  algorithm: 'sma' | 'ema' | 'holt_winters'
  params: Record<string, number>
  horizon_days: number
  history: Point[]
  validation: ForecastPoint[]
  holdout: Point[]
  forecast: ForecastPoint[]
  accuracy: { mae: number; mape: number; rmse: number }
  recommendation: {
    recommended_qty: number
    covers_until: string | null
    standard_cost_total: number | null
    lead_time_days: number
    safety_stock: number
  }
}

type Algo = 'sma' | 'ema' | 'holt_winters'

const ALGO_LABEL: Record<Algo, string> = {
  sma: 'SMA (단순 이동평균)',
  ema: 'EMA (지수 가중평균)',
  holt_winters: 'Holt-Winters (트렌드+계절성)',
}

interface CompareResp {
  item: Item & { history_count: number }
  horizon_days: number
  holdout_size: number
  history: Point[]
  results: Array<{
    name: string
    forecast: ForecastPoint[]
    accuracy: { mae: number; mape: number; rmse: number }
  }>
  best: {
    name: string
    forecast: ForecastPoint[]
    accuracy: { mae: number; mape: number; rmse: number }
  } | null
  recommendation: {
    recommended_qty: number
    covers_until: string | null
    standard_cost_total: number | null
    lead_time_days: number
    safety_stock: number
    best_algorithm: string
    best_mape: number
  } | null
}

// 다중 알고리즘 비교 차트
function CompareChart({ data }: { data: CompareResp }) {
  const W = 880, H = 280
  const padL = 50, padR = 16, padT = 14, padB = 30
  const innerW = W - padL - padR
  const innerH = H - padT - padB

  const lastHistIdx = data.history.length - 1
  const horizon = data.horizon_days
  const totalPoints = data.history.length + horizon

  const allValues: number[] = [
    ...data.history.map((p) => p.value),
    ...data.results.flatMap((r) => r.forecast.map((p) => p.value)),
  ]
  const yMin = Math.max(0, Math.min(...allValues) * 0.9)
  const yMax = Math.max(...allValues) * 1.05 || 1

  const x = (i: number) =>
    padL + (totalPoints <= 1 ? innerW / 2 : (i / (totalPoints - 1)) * innerW)
  const y = (v: number) =>
    padT + (1 - (v - yMin) / (yMax - yMin)) * innerH

  const histLine = data.history
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.value)}`)
    .join(' ')

  const bestName = data.best?.name
  // 색상 풀 (best 제외한 기타용)
  const altColors = ['#94a3b8', '#a78bfa', '#fb923c', '#f472b6', '#facc15', '#34d399']

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="알고리즘 비교 차트" style={{ width: '100%', height: 'auto' }}>
      {/* history */}
      <path d={histLine} fill="none" stroke="var(--color-ink)" strokeWidth="1.8" />
      {/* 기타 알고리즘 라인 (연한 색) */}
      {data.results.filter((r) => r.name !== bestName).map((r, ri) => {
        const path = r.forecast
          .map((p, i) => {
            const idx = lastHistIdx + 1 + i
            return `${i === 0 ? 'M' : 'L'} ${x(idx)} ${y(p.value)}`
          }).join(' ')
        return (
          <path
            key={r.name}
            d={path}
            fill="none"
            stroke={altColors[ri % altColors.length]}
            strokeWidth="1.4"
            strokeDasharray="3 3"
            opacity="0.7"
          >
            <title>{r.name} · MAPE {r.accuracy.mape.toFixed(1)}%</title>
          </path>
        )
      })}
      {/* best 라인 (강조) */}
      {data.best && (
        <path
          d={data.best.forecast.map((p, i) => {
            const idx = lastHistIdx + 1 + i
            return `${i === 0 ? 'M' : 'L'} ${x(idx)} ${y(p.value)}`
          }).join(' ')}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="2.5"
        >
          <title>{data.best.name} (best) · MAPE {data.best.accuracy.mape.toFixed(1)}%</title>
        </path>
      )}
      {/* history 점 */}
      {data.history.map((p, i) => (
        <circle key={`h-${i}`} cx={x(i)} cy={y(p.value)} r="2" fill="var(--color-ink)" />
      ))}
      {/* 분리선 */}
      <line x1={x(lastHistIdx)} y1={padT} x2={x(lastHistIdx)} y2={H - padB} stroke="var(--color-line-strong)" strokeDasharray="2 3" />
      {/* y축 라벨 */}
      {[yMin, (yMin + yMax) / 2, yMax].map((v, i) => (
        <text key={i} x={padL - 6} y={y(v)} textAnchor="end" dominantBaseline="middle" fontSize="10" fill="var(--color-ink-3)">
          {v.toFixed(0)}
        </text>
      ))}
    </svg>
  )
}

function ForecastChart({ data }: { data: RunResp }) {
  const W = 880, H = 280
  const padL = 50, padR = 16, padT = 14, padB = 30
  const innerW = W - padL - padR
  const innerH = H - padT - padB

  // x: 전체 date 배열 = history + forecast
  const allDates = [...data.history.map((p) => p.date), ...data.forecast.map((p) => p.date)]
  const dateIdx = new Map(allDates.map((d, i) => [d, i]))
  const lastHistIdx = data.history.length - 1
  const totalPoints = allDates.length

  const allValues: number[] = [
    ...data.history.map((p) => p.value),
    ...data.forecast.flatMap((p) => [p.value, p.lower ?? p.value, p.upper ?? p.value]),
  ]
  const yMin = Math.max(0, Math.min(...allValues) * 0.9)
  const yMax = Math.max(...allValues) * 1.05 || 1

  const x = (i: number) =>
    padL + (totalPoints <= 1 ? innerW / 2 : (i / (totalPoints - 1)) * innerW)
  const y = (v: number) =>
    padT + (1 - (v - yMin) / (yMax - yMin)) * innerH

  const histLine = data.history
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.value)}`)
    .join(' ')
  const fcLine = data.forecast
    .map((p, i) => {
      const idx = lastHistIdx + 1 + i
      return `${i === 0 ? 'M' : 'L'} ${x(idx)} ${y(p.value)}`
    })
    .join(' ')

  // 신뢰구간 음영 (상한 위로, 하한 아래로 path)
  const fcBand = (() => {
    if (!data.forecast.some((p) => p.lower !== undefined && p.upper !== undefined)) return ''
    const top = data.forecast.map((p, i) => {
      const idx = lastHistIdx + 1 + i
      return `${i === 0 ? 'M' : 'L'} ${x(idx)} ${y(p.upper ?? p.value)}`
    }).join(' ')
    const bottom = [...data.forecast].reverse().map((p, i) => {
      const idx = lastHistIdx + 1 + (data.forecast.length - 1 - i)
      return `L ${x(idx)} ${y(p.lower ?? p.value)}`
    }).join(' ')
    return `${top} ${bottom} Z`
  })()

  // 분리선 (history vs forecast 경계)
  const sepX = x(lastHistIdx)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="수요 예측 차트" style={{ width: '100%', height: 'auto' }}>
      {/* 신뢰구간 음영 */}
      {fcBand && (
        <path d={fcBand} fill="var(--color-accent)" opacity="0.12" />
      )}
      {/* history 라인 */}
      <path d={histLine} fill="none" stroke="var(--color-ink)" strokeWidth="1.8" />
      {/* forecast 라인 (점선) */}
      <path d={fcLine} fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeDasharray="6 4" />
      {/* history 점 */}
      {data.history.map((p, i) => (
        <circle key={`h-${i}`} cx={x(i)} cy={y(p.value)} r="2.5" fill="var(--color-ink)">
          <title>{p.date} · {p.value.toFixed(1)}</title>
        </circle>
      ))}
      {/* forecast 점 */}
      {data.forecast.map((p, i) => {
        const idx = lastHistIdx + 1 + i
        return (
          <circle key={`f-${i}`} cx={x(idx)} cy={y(p.value)} r="3" fill="var(--color-accent)">
            <title>{p.date} · {p.value.toFixed(1)} ({(p.lower ?? 0).toFixed(1)}–{(p.upper ?? 0).toFixed(1)})</title>
          </circle>
        )
      })}
      {/* 경계선 */}
      <line x1={sepX} y1={padT} x2={sepX} y2={H - padB} stroke="var(--color-line-strong)" strokeDasharray="2 3" />
      <text x={sepX + 4} y={padT + 10} fontSize="10" fill="var(--color-ink-3)">예측 시작</text>
      {/* y축 라벨 */}
      {[yMin, (yMin + yMax) / 2, yMax].map((v, i) => (
        <text key={i} x={padL - 6} y={y(v)} textAnchor="end" dominantBaseline="middle" fontSize="10" fill="var(--color-ink-3)">
          {v.toFixed(0)}
        </text>
      ))}
      {/* x축 라벨 */}
      <text x={padL} y={H - 8} fontSize="10" fill="var(--color-ink-3)">{data.history[0]?.date.slice(5)}</text>
      <text x={W - padR} y={H - 8} textAnchor="end" fontSize="10" fill="var(--color-ink-3)">
        {data.forecast[data.forecast.length - 1]?.date.slice(5)}
      </text>
    </svg>
  )
}

export function ForecastingPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'forecast', 'write')

  const [items, setItems] = useState<Item[] | null>(null)
  const [active, setActive] = useState<string | null>(null)
  const [algo, setAlgo] = useState<Algo>('holt_winters')
  const [horizon, setHorizon] = useState(14)
  const [mode, setMode] = useState<'single' | 'compare'>('single')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<RunResp | null>(null)
  const [compareResult, setCompareResult] = useState<CompareResp | null>(null)

  useEffect(() => {
    api.get<Item[]>('/forecasting/items')
      .then((d) => {
        setItems(d)
        const first = d.find((i) => i.history_days >= 14)
        if (first) setActive(first.item_code)
      })
      .catch((e) => { setItems([]); toast.push('error', e?.message ?? '조회 실패') })
  }, [toast])

  const run = async () => {
    if (!active) return
    setBusy(true)
    setResult(null)
    setCompareResult(null)
    try {
      if (mode === 'compare') {
        const r = await api.post<CompareResp>('/forecasting/compare', {
          item_code: active,
          horizon_days: horizon,
        })
        setCompareResult(r)
        if (r.best) {
          toast.push('success', t('fc.compare.done', {
            best: r.best.name,
            mape: r.best.accuracy.mape.toFixed(1),
          }))
        }
      } else {
        const r = await api.post<RunResp>('/forecasting/run', {
          item_code: active,
          algorithm: algo,
          horizon_days: horizon,
        })
        setResult(r)
        toast.push('success', t('fc.runDone', { mape: r.accuracy.mape.toFixed(1) }))
      }
    } catch (e: any) {
      toast.push('error', e?.message ?? t('fc.fail'))
    } finally { setBusy(false) }
  }

  const activeItem = useMemo(() => items?.find((i) => i.item_code === active) ?? null, [items, active])

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <div className="row space-between reveal">
        <div>
          <h1 className="page-title" style={{ marginBottom: '0.2rem' }}>
            <FontAwesomeIcon icon="chart-line" /> {t('fc.title')}
          </h1>
          <p className="muted" style={{ marginBottom: 0 }}>
            {t('fc.intro')}
          </p>
        </div>
      </div>

      {/* 컨트롤 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section">
        <div className="row" style={{ gap: '0.4rem', marginBottom: '0.75rem' }}>
          <Button
            variant={mode === 'single' ? 'accent' : 'ghost'}
            magnetic={false}
            onClick={() => setMode('single')}
          >{t('fc.mode.single')}</Button>
          <Button
            variant={mode === 'compare' ? 'accent' : 'ghost'}
            magnetic={false}
            onClick={() => setMode('compare')}
          >
            <FontAwesomeIcon icon="scale-balanced" /> {t('fc.mode.compare')}
          </Button>
        </div>
        <div className="row" style={{ alignItems: 'end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
            <label htmlFor="item-sel">{t('fc.field.item')}</label>
            <select id="item-sel" value={active ?? ''} onChange={(e) => setActive(e.target.value)} disabled={!items}>
              {!items && <option>{t('common.loading')}</option>}
              {items?.map((i) => (
                <option key={i.item_code} value={i.item_code} disabled={i.history_days < 14}>
                  {i.item_code} — {i.name} ({i.history_days}d)
                </option>
              ))}
            </select>
          </div>
          {mode === 'single' && (
            <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
              <label htmlFor="algo-sel">{t('fc.field.algo')}</label>
              <select id="algo-sel" value={algo} onChange={(e) => setAlgo(e.target.value as Algo)}>
                {(Object.keys(ALGO_LABEL) as Algo[]).map((a) => (
                  <option key={a} value={a}>{ALGO_LABEL[a]}</option>
                ))}
              </select>
            </div>
          )}
          <div className="field" style={{ flex: '0 1 120px', marginBottom: 0 }}>
            <label htmlFor="hz">{t('fc.field.horizon')}</label>
            <input id="hz" type="number" min="1" max="60" value={horizon}
              onChange={(e) => setHorizon(Math.max(1, Math.min(60, Number(e.target.value))))} />
          </div>
          <Button variant="accent" magnetic={false} disabled={!canWrite || busy || !active} onClick={run}>
            <FontAwesomeIcon icon={busy ? 'circle-notch' : 'bolt'} spin={busy} />
            <span>{mode === 'compare' ? t('fc.compare.run') : t('fc.run')}</span>
          </Button>
        </div>
      </Card>

      {/* 비교 결과 (compare 모드 전용) */}
      {compareResult && compareResult.best && (
        <>
          <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="cmp-chart-h">
            <div className="row space-between" style={{ marginBottom: '0.5rem' }}>
              <h2 id="cmp-chart-h" style={{ fontSize: 'var(--fs-lg)' }}>
                <FontAwesomeIcon icon="scale-balanced" /> {t('fc.compare.title')}
              </h2>
              <span className="badge success">
                <FontAwesomeIcon icon="bolt" /> {t('fc.compare.bestBadge')} · {compareResult.best.name}
              </span>
            </div>
            <CompareChart data={compareResult} />
            <div className="row" style={{ marginTop: '0.6rem', gap: '1rem', flexWrap: 'wrap', fontSize: 'var(--fs-sm)' }}>
              <span className="row" style={{ gap: '0.3rem' }}>
                <span style={{ width: 24, height: 2, background: 'var(--color-ink)' }} /> {t('fc.compare.legend.actual')}
              </span>
              <span className="row" style={{ gap: '0.3rem' }}>
                <span style={{ width: 24, height: 2.5, background: 'var(--color-accent)' }} /> {t('fc.compare.legend.best', { name: compareResult.best.name })}
              </span>
              <span className="row" style={{ gap: '0.3rem' }}>
                <span style={{ width: 24, height: 1.4, background: 'var(--color-ink-3)', backgroundImage: 'repeating-linear-gradient(to right, var(--color-ink-3) 0 3px, transparent 3px 6px)' }} /> {t('fc.compare.legend.other')}
              </span>
            </div>
          </Card>

          <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="cmp-rank-h">
            <h2 id="cmp-rank-h" style={{ fontSize: 'var(--fs-lg)' }}>{t('fc.compare.title')}</h2>
            <div className="table-wrap" style={{ '--table-min-w': '480px' } as React.CSSProperties}>
              <table className="table" aria-label={t('fc.compare.title')}>
                <thead>
                  <tr>
                    <th>{t('fc.compare.col.rank')}</th>
                    <th>{t('fc.compare.col.algo')}</th>
                    <th>{t('fc.compare.col.mape')}</th>
                    <th>{t('fc.compare.col.mae')}</th>
                    <th>{t('fc.compare.col.rmse')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...compareResult.results].sort((a, b) => a.accuracy.mape - b.accuracy.mape).map((r, i) => (
                    <tr key={r.name} style={r.name === compareResult.best?.name ? { background: 'color-mix(in oklab, var(--color-success) 12%, transparent)' } : undefined}>
                      <td className="mono">{i + 1}</td>
                      <td>
                        <strong>{r.name}</strong>
                        {r.name === compareResult.best?.name && (
                          <span className="badge success" style={{ marginLeft: '0.4rem' }}>{t('fc.compare.bestBadge')}</span>
                        )}
                      </td>
                      <td className="mono">{r.accuracy.mape.toFixed(2)}%</td>
                      <td className="mono">{r.accuracy.mae.toFixed(2)}</td>
                      <td className="mono">{r.accuracy.rmse.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {compareResult.recommendation && (
            <section className="reveal" style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <Card glass tilt={false} spotlight={false}>
                <span className="badge info"><FontAwesomeIcon icon="calculator" /> {t('fc.kpi.mape')}</span>
                <p className="mega-num" style={{ margin: '0.4rem 0 0' }}>
                  <Counter value={compareResult.recommendation.best_mape} decimals={1} suffix="%" />
                </p>
                <p className="muted" style={{ marginBottom: 0, fontSize: 'var(--fs-sm)' }}>
                  {compareResult.recommendation.best_algorithm}
                </p>
              </Card>
              <Card glass tilt={false} spotlight={false}>
                <span className="badge success"><FontAwesomeIcon icon="boxes-stacked" /> {t('fc.kpi.recommended')}</span>
                <p className="kpi-num">
                  <Counter value={compareResult.recommendation.recommended_qty} suffix={` ${compareResult.item.uom ?? ''}`} />
                </p>
                <p className="muted" style={{ marginBottom: 0, fontSize: 'var(--fs-sm)' }}>
                  {t('fc.kpi.recommended.note', {
                    lead: compareResult.recommendation.lead_time_days,
                    safety: compareResult.recommendation.safety_stock,
                  })}
                </p>
              </Card>
              {compareResult.recommendation.standard_cost_total !== null && (
                <Card glass tilt={false} spotlight={false}>
                  <span className="badge warn"><FontAwesomeIcon icon="calculator" /> {t('fc.kpi.cost')}</span>
                  <p className="kpi-num">
                    ₩<Counter value={compareResult.recommendation.standard_cost_total} />
                  </p>
                </Card>
              )}
            </section>
          )}
        </>
      )}

      {/* 단일 결과 */}
      {!result && !compareResult && !busy && !items ? (
        <Card glass tilt={false} spotlight={false} className="reveal"><SkeletonRows rows={6} /></Card>
      ) : busy ? (
        <Card glass tilt={false} spotlight={false} className="reveal">
          <Skeleton h={280} />
        </Card>
      ) : !result && !compareResult ? (
        <Card glass tilt={false} spotlight={false} className="reveal">
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="chart-line" className="icon-lg" />
            <p className="muted">{t('fc.empty')}</p>
          </div>
        </Card>
      ) : result ? (
        <>
          {/* KPI */}
          <section className="reveal" style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Card glass tilt={false} spotlight={false}>
              <span className="badge info"><FontAwesomeIcon icon="calculator" /> 예측 정확도 (MAPE)</span>
              <p className="mega-num" style={{ margin: '0.4rem 0 0' }}>
                <Counter value={result.accuracy.mape} decimals={1} suffix="%" />
              </p>
              <p className="muted" style={{ marginBottom: 0, fontSize: 'var(--fs-sm)' }}>
                낮을수록 좋음 · holdout 검증
              </p>
            </Card>
            <Card glass tilt={false} spotlight={false}>
              <span className="badge"><FontAwesomeIcon icon="ruler-combined" /> MAE</span>
              <p className="kpi-num">
                <Counter value={result.accuracy.mae} decimals={1} suffix={` ${activeItem?.uom ?? ''}`} />
              </p>
            </Card>
            <Card glass tilt={false} spotlight={false}>
              <span className="badge success"><FontAwesomeIcon icon="boxes-stacked" /> 추천 발주량</span>
              <p className="kpi-num">
                <Counter value={result.recommendation.recommended_qty} suffix={` ${activeItem?.uom ?? ''}`} />
              </p>
              <p className="muted" style={{ marginBottom: 0, fontSize: 'var(--fs-sm)' }}>
                Lead time {result.recommendation.lead_time_days}일 + 안전재고 {result.recommendation.safety_stock}
              </p>
            </Card>
            {result.recommendation.standard_cost_total !== null && (
              <Card glass tilt={false} spotlight={false}>
                <span className="badge warn"><FontAwesomeIcon icon="calculator" /> 표준 원가</span>
                <p className="kpi-num">
                  ₩<Counter value={result.recommendation.standard_cost_total} />
                </p>
              </Card>
            )}
          </section>

          {/* 차트 */}
          <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="chart-h">
            <div className="row space-between" style={{ marginBottom: '0.5rem' }}>
              <h2 id="chart-h" style={{ fontSize: 'var(--fs-lg)' }}>{ALGO_LABEL[result.algorithm]}</h2>
              <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                과거 {result.history.length}일 · 예측 {result.horizon_days}일
              </span>
            </div>
            <ForecastChart data={result} />
            <div className="row" style={{ marginTop: '0.6rem', gap: '1rem', flexWrap: 'wrap', fontSize: 'var(--fs-sm)' }}>
              <span className="row" style={{ gap: '0.3rem' }}>
                <span style={{ width: 24, height: 2, background: 'var(--color-ink)' }} /> 실측
              </span>
              <span className="row" style={{ gap: '0.3rem' }}>
                <span style={{ width: 24, height: 2, background: 'var(--color-accent)', backgroundImage: 'repeating-linear-gradient(to right, var(--color-accent) 0 6px, transparent 6px 10px)' }} /> 예측
              </span>
              <span className="row" style={{ gap: '0.3rem' }}>
                <span style={{ width: 18, height: 10, background: 'var(--color-accent)', opacity: 0.12 }} /> 95% 신뢰구간
              </span>
            </div>
          </Card>

          {/* 예측 테이블 */}
          <Card glass tilt={false} spotlight={false} className="reveal" as="section">
            <h2 style={{ fontSize: 'var(--fs-lg)' }}>예측 상세</h2>
            <div className="table-wrap" style={{ '--table-min-w': '520px' } as React.CSSProperties}>
              <table className="table" aria-label="예측 값 테이블">
                <thead>
                  <tr><th>일자</th><th>예측</th><th>하한 (95%)</th><th>상한 (95%)</th></tr>
                </thead>
                <tbody>
                  {result.forecast.map((p) => (
                    <tr key={p.date}>
                      <td>{p.date}</td>
                      <td className="mono">{p.value.toFixed(1)}</td>
                      <td className="mono muted">{(p.lower ?? 0).toFixed(1)}</td>
                      <td className="mono muted">{(p.upper ?? 0).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : null}
    </main>
  )
}
